<template>
  <div>
    <v-dialog
      v-model="internalValue"
      max-width="900px"
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header" data-tour="cas-header">
          <v-icon color="white" class="mr-2" style="position: relative; z-index: 1;">mdi-account-group</v-icon>
          <span class="ds-dialog-title">Assistentes desta Clínica</span>
          <v-spacer />
          <v-btn icon color="white" aria-label="Ver tour" class="close-btn" @click="startTour">
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn icon color="white" class="close-btn" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <v-stepper v-model="step" flat class="transparent">
            <!-- STEPS HEADER -->
            <v-stepper-header class="steps-header" data-tour="cas-steps">
              <v-stepper-step :complete="step > 1" step="1">
                Assistentes Vinculados
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step step="2">
                {{
                  isEditMode ? "Editar Assistente" : "Cadastrar Novo Assistente"
                }}
              </v-stepper-step>
            </v-stepper-header>

            <!-- STEPS CONTENT -->
            <v-stepper-items>
              <!-- STEP 1: LISTA DE ASSISTENTES -->
              <v-stepper-content step="1" class="px-0 pb-0">
                <!-- Informações da clínica -->
                <section v-if="selectedClinicInfo" class="ds-form-section" data-tour="cas-clinic-info">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-hospital-building</v-icon>
                      <span>Clínica</span>
                    </div>
                  </div>
                  <div class="clinic-info-name">
                    {{ selectedClinicInfo.name }}
                  </div>
                  <div class="clinic-info-meta">
                    CNPJ: {{ selectedClinicInfo.cnpj }} -
                    {{ selectedClinicInfo.city }}/{{
                      selectedClinicInfo.state
                    }}
                  </div>
                </section>

                <section class="ds-form-section">
                  <div class="ds-form-section__head" data-tour="cas-list">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-account-tie</v-icon>
                      <span>Assistentes Vinculados</span>
                    </div>
                    <span v-if="!loadingRelatedAssistants" class="ds-form-section__aside">{{ assistantsRelated.length }}</span>
                  </div>

                  <v-container fluid class="pa-0">
                    <v-row
                      v-if="loadingRelatedAssistants"
                      justify="center"
                      class="my-10"
                    >
                      <v-progress-circular
                        indeterminate
                        color="primary"
                        size="50"
                      ></v-progress-circular>
                    </v-row>

                    <v-row
                      v-if="
                        !loadingRelatedAssistants && assistantsRelated.length
                      "
                      dense
                    >
                      <v-col
                        v-for="(assistant, i) in assistantsRelated"
                        :key="assistant.id"
                        cols="12"
                        sm="6"
                        md="4"
                      >
                        <v-card
                          outlined
                          class="assistant-card h-100"
                          hover
                          :data-tour="i === 0 ? 'cas-assistant-card' : null"
                        >
                          <v-card-title class="pa-3 flex-nowrap align-start">
                            <v-avatar size="50" class="mr-3 flex-shrink-0">
                              <v-img :src="assistant.imageUrl" />
                            </v-avatar>
                            <div class="flex-grow-1" style="min-width: 0">
                              <div
                                class="font-weight-medium text-caption text-truncate"
                                :title="assistant.name"
                              >
                                {{ assistant.name }}
                              </div>
                              <div
                                class="text-caption grey--text text-truncate"
                                :title="assistant.email"
                              >
                                {{ assistant.email }}
                              </div>
                              <div
                                class="text-caption grey--text text-truncate"
                                v-if="assistant.cpf"
                              >
                                CPF: {{ assistant.cpf }}
                              </div>
                            </div>
                          </v-card-title>

                          <!-- AÇÕES -->
                          <v-card-actions
                            class="pa-3 pt-0"
                            :data-tour="i === 0 ? 'cas-actions' : null"
                          >
                            <v-btn
                              icon
                              color="primary"
                              @click="editAssistant(assistant)"
                            >
                              <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn
                              icon
                              color="red"
                              @click="openDeleteDialog(assistant)"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-col>
                    </v-row>

                    <div
                      v-else-if="!loadingRelatedAssistants"
                      class="text-center py-6"
                    >
                      <v-icon size="64" color="#C2DDD3" class="mb-3">
                        mdi-account-heart
                      </v-icon>
                      <div class="empty-state-title mb-2">
                        Nenhum assistente vinculado
                      </div>
                      <div class="empty-state-subtitle">
                        Esta clínica ainda não possui assistentes vinculados
                      </div>
                    </div>
                  </v-container>

                  <div class="mt-4 text-right" data-tour="cas-new">
                    <v-btn color="primary" depressed @click="goToStep2()">
                      <v-icon left>mdi-plus</v-icon>
                      Novo Assistente
                    </v-btn>
                  </div>
                </section>
              </v-stepper-content>

              <!-- STEP 2: CADASTRAR ASSISTENTE -->
              <v-stepper-content step="2" class="px-0 pb-0">
                <section class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-account-plus</v-icon>
                      <span>Cadastrar Assistente</span>
                    </div>
                  </div>

                  <v-form ref="assistantForm" v-model="formValido">
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="assistant.email"
                          label="Email"
                          prepend-inner-icon="mdi-email-outline"
                          clearable
                          outlined
                          color="primary"
                          type="email"
                          :rules="[
                            (v) => !!v || 'Email obrigatório',
                            (v) => /.+@.+\..+/.test(v) || 'Email inválido',
                          ]"
                        />
                      </v-col>
                    </v-row>
                  </v-form>

                  <div class="d-flex align-center mt-4">
                    <v-btn
                      outlined
                      color="primary"
                      @click="step = 1"
                    >
                      <v-icon left>mdi-close</v-icon>
                      Cancelar
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="primary"
                      :loading="loading"
                      dark
                      depressed
                      @click="saveAssistant"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Salvar
                    </v-btn>
                  </div>
                </section>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { getMyClinics, getAssistantsByClinics } from "@/services/clinicService";
import { getProfileImage } from "@/utils/getProfileImage";
import tourDialogMixin from "@/features/tour/tourDialogMixin";

export default {
  name: "CheckMyClinicAssistantsDialog",

  mixins: [tourDialogMixin],

  props: {
    value: { type: Boolean, default: false },
    clinicId: { type: Number, default: null },
  },

  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },

  data() {
    return {
      // dialog / stepper
      dialogVisible: false,
      step: 1,

      // clinic
      clinics: [],
      selectedClinicId: null,
      selectedClinicInfo: null,

      // assistants list
      assistantsRelated: [],
      loadingRelatedAssistants: false,

      // form
      formValido: false,
      loading: false,
      erro: "",
      assistant: {
        email: "",
        clinic_id: null,
      },

      // edit mode
      isEditMode: false,
      editingAssistantId: null,

      // delete confirm dialog
      assistantToDelete: null,
      loadingDelete: false,

      // blob urls (para revogar)
      _assistantBlobUrls: new Set(),
    };
  },

  computed: {
    userType() {
      return this.$store.getters["auth/userType"];
    },
    me() {
      return this.$store.getters["auth/user"];
    },

    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    tourId() {
      return "dialog-assistentes-clinica";
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.initDialog();
        this.tourOnOpen();
      }
    },
  },

  async mounted() {
    await this.$store.dispatch("auth/ensureAuth");
    await this.fetchClinics();
  },

  beforeDestroy() {
    this.revokeAssistantBlobs();
  },

  methods: {
    // ================= TOUR =================
    tourReady() {
      return !this.loadingRelatedAssistants;
    },

    tourSteps() {
      return [
        {
          title: "Assistentes da Clínica",
          description:
            "Aqui você gerencia todos os assistentes vinculados a esta clínica. Vou te guiar rapidinho pela tela.",
        },
        {
          anchor: "cas-header",
          title: "Cabeçalho",
          description:
            "Este é o topo do diálogo. O botão de interrogação reabre este tour e o X fecha a janela quando quiser.",
        },
        {
          anchor: "cas-steps",
          title: "Etapas",
          description:
            "O fluxo tem duas etapas: a lista de assistentes vinculados e o cadastro de um novo assistente. Acompanhe por aqui em qual etapa você está.",
        },
        {
          anchor: "cas-clinic-info",
          title: "Dados da Clínica",
          description:
            "Este cartão mostra o nome, CNPJ e cidade da clínica atual, para não haver dúvida sobre onde os assistentes estão sendo vinculados.",
        },
        {
          anchor: "cas-list",
          title: "Assistentes Vinculados",
          description:
            "Abaixo desta seção ficam listados todos os assistentes já vinculados a esta clínica.",
        },
        {
          anchor: "cas-assistant-card",
          title: "Cartão do Assistente",
          description:
            "Cada assistente aparece em um cartão com foto, nome, e-mail e CPF, facilitando a identificação de cada pessoa da equipe.",
        },
        {
          anchor: "cas-actions",
          title: "Ações do Assistente",
          description:
            "No cartão você pode editar os dados do assistente pelo lápis ou remover o vínculo dele com a clínica pela lixeira.",
        },
        {
          anchor: "cas-new",
          title: "Cadastrar Novo Assistente",
          description:
            "Use este botão para convidar e vincular um novo assistente à clínica informando o e-mail dele. Pronto, você já conhece a tela!",
        },
      ];
    },

    // ================= CLÍNICAS =================
    async fetchClinics() {
      try {
        this.clinics = (await getMyClinics()) || [];
      } catch (err) {
        console.error("Erro ao buscar clínicas:", err);
        this.erroDeRequisicao(err, "Erro ao carregar clínicas");
        this.clinics = [];
      }
    },

    async initDialog() {
      this.selectedClinicId = this.clinicId;
      this.step = 1;
      this.selectedClinicInfo =
        this.clinics.find((c) => c.id === this.clinicId) || null;
      await this.fetchRelatedAssistants();
    },

    // ================= ASSISTENTES =================
    async fetchRelatedAssistants() {
      if (!this.selectedClinicId) return;

      this.loadingRelatedAssistants = true;

      try {
        const assistants =
          (await getAssistantsByClinics(this.selectedClinicId)) || [];

        this.assistantsRelated = await Promise.all(
          assistants.map(async (assistant) => {
            const imageUrl = await getProfileImage(assistant.profile_image);

            if (typeof imageUrl === "string" && imageUrl.startsWith("blob:")) {
              this._assistantBlobUrls.add(imageUrl);
            }

            return { ...assistant, imageUrl };
          }),
        );
      } catch (err) {
        console.error("Erro ao buscar assistentes:", err);
        this.erroDeRequisicao(err, "Erro ao carregar assistentes da clínica");
        this.assistantsRelated = [];
      } finally {
        this.loadingRelatedAssistants = false;
      }
    },

    revokeAssistantBlobs() {
      if (!(this._assistantBlobUrls instanceof Set)) {
        this._assistantBlobUrls = new Set();
        return;
      }

      for (const url of this._assistantBlobUrls) {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }

      this._assistantBlobUrls.clear();
    },

    // ================= STEPS =================
    goToStep2() {
      this.resetAssistantForm();
      this.isEditMode = false;
      this.editingAssistantId = null;
      this.step = 2;
    },

    async editAssistant(assistant) {
      this.isEditMode = true;
      this.editingAssistantId = assistant.id;
      await this.loadAssistant(assistant.id);
      this.step = 2;
    },

    // ================= EXCLUSÃO =================
    async openDeleteDialog(assistant) {
      this.assistantToDelete = assistant;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: `Tem certeza que deseja excluir este assistente?\n${assistant?.name || ""}${assistant?.email ? " - " + assistant.email : ""}`,
        hint: "Esta ação não pode ser desfeita.",
      });
      if (!ok) {
        this.assistantToDelete = null;
        return;
      }
      await this.deleteAssistant(assistant.id);
    },

    async deleteAssistant(id) {
      this.loadingDelete = true;

      try {
        await http.delete(`/api/assistants/delete/${id}`);
        this.success("Assistente deletado!");
        await this.fetchRelatedAssistants();
      } catch (err) {
        console.error(err);
        this.erroDeRequisicao(err, "Erro ao deletar assistente");
      } finally {
        this.loadingDelete = false;
        this.assistantToDelete = null;
      }
    },

    // ================= CARREGAR/EDITAR =================
    async loadAssistant(id) {
      try {
        const { data } = await http.get(`/api/user/get-user/${id}`);
        const assistantData = data?.data || data;

        if (!assistantData) {
          throw new Error("Dados do assistente não encontrados");
        }

        this.assistant = {
          email: assistantData.email || "",
          clinic_id: assistantData.clinic_id || this.selectedClinicId,
        };
      } catch (err) {
        console.error("Erro ao carregar assistente:", err);
        this.erroDeRequisicao(err, "Erro ao carregar dados do assistente.");
      }
    },

    resetAssistantForm() {
      this.assistant = { email: "", clinic_id: null };

      this.erro = "";
      this.loading = false;
      this.editingAssistantId = null;
      this.isEditMode = false;

      this.$refs.assistantForm?.resetValidation?.();
    },

    async saveAssistant() {
      const valido = this.$refs.assistantForm?.validate?.();
      if (!valido) return this.error("Por favor, informe um email válido.");
      if (!this.selectedClinicId) return this.error("Selecione uma clínica.");

      this.loading = true;

      try {
        await http.post(`/api/clinics/invite-user/${this.selectedClinicId}`, {
          email: this.assistant.email,
          role_id: 3,
        });

        this.success("Assistente cadastrado com sucesso!");
        await this.fetchRelatedAssistants();
        this.step = 1;
        this.resetAssistantForm();
      } catch (err) {
        if (err.response?.status === 422) {
          const errors = err.response.data?.errors;
          const msg = errors
            ? Object.values(errors)[0]?.[0] || "Erro de validação."
            : "Erro de validação.";
          this.erroDeRequisicao(err, msg);
        } else {
          this.erroDeRequisicao(err, "Erro ao salvar assistente.");
        }
      } finally {
        this.loading = false;
      }
    },

    // ================= UI =================
    closeDialog() {
      this.$emit("input", false);
      this.selectedClinicId = null;
      this.selectedClinicInfo = null;
      this.assistantsRelated = [];
      this.step = 1;
      this.resetAssistantForm();
      this.revokeAssistantBlobs();
    },
  },
};
</script>

<style scoped>
/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-header--danger {
  background: linear-gradient(135deg, var(--ds-danger) 0%, var(--ds-danger-mid) 60%, var(--ds-danger-light) 100%) !important;
}

.close-btn {
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Cabeçalho do stepper: cartão branco sobre o corpo areia ═══ */
.steps-header {
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: var(--ds-radius);
  box-shadow: none !important;
}

/* ═══ Info da clínica ═══ */
.clinic-info-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.2px;
}

.clinic-info-meta {
  font-size: 11px;
  color: var(--ds-primary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
}

/* ═══ Assistant card ═══ */
.assistant-card {
  border-radius: 12px !important;
  border: 1px solid var(--ds-blue-100) !important;
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.04) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.assistant-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(31, 77, 63, 0.16) !important;
  border-color: var(--ds-blue-200) !important;
}

.assistant-card :deep(.v-avatar) {
  border: 2px solid var(--ds-blue-100);
}

.assistant-card :deep(.font-weight-medium) {
  font-size: 13px;
  color: var(--ds-text-strong);
  font-weight: 700 !important;
}

/* ═══ Empty state ═══ */
.empty-state-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ds-primary);
  letter-spacing: -0.2px;
}

.empty-state-subtitle {
  font-size: 12px;
  color: var(--ds-text-soft);
  font-weight: 500;
  line-height: 1.5;
}
</style>
